import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Table, Statistic, Row, Col, Divider, Empty, Tag, Typography, Tabs } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';

// Define styles outside of the component to avoid recreation on each render and fix lint warnings
const COMPONENT_STYLES = `
  .segment-summary-card .statistics-container {
    margin-bottom: 16px;
  }
  
  .segment-summary-card .stat-card {
    height: 100%;
    background-color: #fafafa;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    transition: all 0.3s;
  }
  
  .segment-summary-card .stat-card:hover {
    box-shadow: 0 3px 6px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  .segment-summary-card .ant-statistic-title {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .segment-summary-card .ant-statistic-content {
    font-size: 20px;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    .segment-summary-card .ant-statistic-title {
      font-size: 12px;
    }
    
    .segment-summary-card .ant-statistic-content {
      font-size: 16px;
    }
  }
`;

const SelectedSegmentSummary = ({ onReset, forceUpdate = 0 }) => {
  // Campaign-based state
  const [campaignSegments, setCampaignSegments] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  console.log(campaigns, "campaigns 1:")
  console.log(campaignSegments, "campaignSegments 1:")
  const [activeCampaign, setActiveCampaign] = useState(null);
  
  // Legacy state for backward compatibility - still needed for UI rendering
  const [selectedSegments, setSelectedSegments] = useState([]);
  
  // Stats state - now campaign-specific
  const [campaignStats, setCampaignStats] = useState({});
  
  // Default stats structure - wrapped in useMemo to prevent recreation on each render
  const defaultStats = useMemo(() => ({
    totalMailedQuantity: 0,
    averagePerformance: 0,
    totalGrossOrders: 0,
    averageGrossPercentage: 0,
    totalNetProfitLoss: 0,
    averagePLPerBuyer: 0,
    totalDelta: 0,
    totalPLPerBuyer: 0,
    totalPLPerBuyerTotal: 0
  }), []);

  // Inject custom styles - using a ref to track the style element
  const styleElementRef = React.useRef(null);
  
  useEffect(() => {
    // Create style element if it doesn't exist
    if (!styleElementRef.current) {
      const styleElement = document.createElement('style');
      styleElement.innerHTML = COMPONENT_STYLES;
      document.head.appendChild(styleElement);
      styleElementRef.current = styleElement;
    }
    
    // Cleanup function
    return () => {
      if (styleElementRef.current) {
        document.head.removeChild(styleElementRef.current);
        styleElementRef.current = null;
      }
    };
  }, []);

  // Wrap calculateTotals in useCallback to prevent recreation on each render
  const calculateTotals = useCallback((segments) => {
    if (!segments || segments.length === 0) {
      return defaultStats;
    }
    
    const totalMailed = segments.reduce((sum, segment) => sum + (segment.mailed_quantity || 0), 0);
    const grossOrders = segments.reduce((sum, segment) => sum + (segment.gross_orders || 0), 0);
    const netProfitLoss = segments.reduce((sum, segment) => sum + (segment.net_profit_loss || 0), 0);
    const delta = segments.reduce((sum, segment) => sum + (segment.delta || 0), 0);
    const plPerBuyer = segments.reduce((sum, segment) => sum + (segment.be_pl_per_buyer_ltv || 0), 0);
    const plPerBuyerTotal = segments.reduce((sum, segment) => sum + (segment.fe_pl_per_buyer_ltv || 0), 0);
    
    // Calculate weighted average performance based on mailed quantity
    const weightedPerformanceSum = segments.reduce((sum, segment) => {
      return sum + (segment.performance || 0) * (segment.mailed_quantity || 0);
    }, 0);
    
    // Calculate weighted average gross percentage
    const weightedGrossPercentageSum = segments.reduce((sum, segment) => {
      return sum + (segment.gross_percentage || 0) * (segment.mailed_quantity || 0);
    }, 0);
    
    // Calculate weighted average PL per buyer
    const weightedPLPerBuyerSum = segments.reduce((sum, segment) => {
      return sum + (segment.net_profit_loss_per_buyer || 0) * (segment.mailed_quantity || 0);
    }, 0);
    
    const avgPerformance = totalMailed > 0 ? weightedPerformanceSum / totalMailed : 0;
    const avgGrossPercentage = totalMailed > 0 ? weightedGrossPercentageSum / totalMailed : 0;
    const avgPLPerBuyer = totalMailed > 0 ? weightedPLPerBuyerSum / totalMailed : 0;
    
    return {
      totalMailedQuantity: totalMailed,
      averagePerformance: avgPerformance,
      totalGrossOrders: grossOrders,
      averageGrossPercentage: avgGrossPercentage,
      totalNetProfitLoss: netProfitLoss,
      averagePLPerBuyer: avgPLPerBuyer,
      totalDelta: delta,
      totalPLPerBuyer: plPerBuyer,
      totalPLPerBuyerTotal: plPerBuyerTotal
    };
  }, [defaultStats]);


  // Load selected segments from localStorage on component mount or when forceUpdate changes
  useEffect(() => {
    // Load campaign segments from localStorage
    const loadCampaignSegments = () => {
      const savedCampaignSegments = localStorage.getItem('campaignSegments');
      let campaignData = {};
      let campaignList = [];
      let firstCampaign = null;
      let stats = {};
      
      // For backward compatibility, also check for legacy selectedSegments
      const savedLegacySegments = localStorage.getItem('selectedSegments');
      
      if (savedCampaignSegments) {
        campaignData = JSON.parse(savedCampaignSegments);
        campaignList = Object.keys(campaignData).map(id => ({
          id,
          name: campaignData[id].campaignName || `Campaign ${id}`
        }));
        
        if (campaignList.length > 0) {
          firstCampaign = campaignList[0].id;
        }
        
        // Initialize stats for each campaign (supports array or object-with-segments)
        Object.keys(campaignData).forEach(campaignId => {
          const segs = Array.isArray(campaignData[campaignId])
            ? campaignData[campaignId]
            : (campaignData[campaignId].segments || []);
          stats[campaignId] = calculateTotals(segs);
        });
        
        setCampaignSegments(campaignData);
        setCampaigns(campaignList);
        setCampaignStats(stats);
        setActiveCampaign(firstCampaign);
        
        // For backward compatibility, also set selectedSegments
        if (firstCampaign && campaignData[firstCampaign]) {
          const segs = Array.isArray(campaignData[firstCampaign])
            ? campaignData[firstCampaign]
            : (campaignData[firstCampaign].segments || []);
          setSelectedSegments(segs);
        }
      } else if (savedLegacySegments) {
        // Handle legacy data format for backward compatibility
        const parsedSegments = JSON.parse(savedLegacySegments);
        setSelectedSegments(parsedSegments);
        
        // Create a default campaign with the legacy segments
        const defaultCampaignId = 'default';
        campaignData = {
          [defaultCampaignId]: {
            campaignName: 'Default Campaign',
            segments: parsedSegments
          }
        };
        
        campaignList = [{ id: defaultCampaignId, name: 'Default Campaign' }];
        
        stats = {
          [defaultCampaignId]: calculateTotals(parsedSegments)
        };
        
        setCampaignSegments(campaignData);
        setCampaigns(campaignList);
        setCampaignStats(stats);
        setActiveCampaign(defaultCampaignId);
      } else {
        // No data at all
        setSelectedSegments([]);
        setCampaignSegments({});
        setCampaigns([]);
        setCampaignStats({});
        setActiveCampaign(null);
      }
    };
    
    loadCampaignSegments();
  }, [forceUpdate, calculateTotals]);

  // Calculate totals whenever selected segments change


  // Handle reset for a specific campaign
  const handleResetCampaign = (campaignId) => {
    if (!campaignId) return;
    
    // Update campaign segments
    const updatedCampaignSegments = { ...campaignSegments };
    
    if (updatedCampaignSegments[campaignId]) {
      if (Array.isArray(updatedCampaignSegments[campaignId])) {
        // If stored as array, clear it
        updatedCampaignSegments[campaignId] = [];
      } else {
        // If stored as object-with-segments, clear segments
        updatedCampaignSegments[campaignId].segments = [];
      }
      
      // Update localStorage
      localStorage.setItem('campaignSegments', JSON.stringify(updatedCampaignSegments));
      
      // Update state
      setCampaignSegments(updatedCampaignSegments);
      setCampaignStats({
        ...campaignStats,
        [campaignId]: { ...defaultStats }
      });
      
      // If this is the active campaign, also update selectedSegments for UI
      if (activeCampaign === campaignId) {
        setSelectedSegments([]);
      }
      
      // Call onReset callback if provided
      if (onReset) onReset();
    }
  };

  // Handle reset for all campaigns (legacy behavior)
  const handleResetAll = () => {
    setSelectedSegments([]);
    setCampaignSegments({});
    setCampaigns([]);
    setCampaignStats({});
    setActiveCampaign(null);
    
    // Clear localStorage
    localStorage.removeItem('selectedSegments');
    localStorage.removeItem('campaignSegments');
    
    if (onReset) onReset();
  };

  // Handle removing a single segment
  // Handle removing a segment from a specific campaign
  const handleRemoveSegment = (segmentId, campaignId = activeCampaign) => {
    if (!campaignId || !campaignSegments[campaignId]) return;
    
    // Get the segments for this campaign
    const campaignData = campaignSegments[campaignId];
    const updatedSegments = (Array.isArray(campaignData)
      ? campaignData.filter(segment => segment.id !== segmentId)
      : (campaignData.segments || []).filter(segment => segment.id !== segmentId));
    
    // Update campaign segments
    const updatedCampaignSegments = { ...campaignSegments };
    if (Array.isArray(campaignData)) {
      updatedCampaignSegments[campaignId] = updatedSegments;
    } else {
      updatedCampaignSegments[campaignId].segments = updatedSegments;
    }
    
    // Update localStorage
    localStorage.setItem('campaignSegments', JSON.stringify(updatedCampaignSegments));
    
    // Update state
    setCampaignSegments(updatedCampaignSegments);
    setCampaignStats({
      ...campaignStats,
      [campaignId]: calculateTotals(updatedSegments)
    });
    
    // If this is the active campaign, also update selectedSegments for UI
    if (activeCampaign === campaignId) {
      setSelectedSegments(updatedSegments);
    }
  };

  // Table columns for selected segments
  const columns = [
    {
      title: 'List Name',
      dataIndex: 'list_name',
      key: 'list_name',
    },
    {
      title: 'Segment Name',
      dataIndex: 'segment_name',
      key: 'segment_name',
    },
    {
      title: 'Mailed Qty',
      dataIndex: 'mailed_quantity',
      key: 'mailed_quantity',
      render: (text) => text?.toLocaleString() || '0',
    },
    {
      title: 'Gross Orders',
      dataIndex: 'gross_orders',
      key: 'gross_orders',
      render: (text) => text?.toLocaleString() || '0',
    },
    {
      title: 'Gross %',
      dataIndex: 'gross_percentage',
      key: 'gross_percentage',
      render: (text) => ((parseFloat(text) || 0) * 100).toFixed(2) + '%',
    },

    {
      title: 'Net P/L',
      dataIndex: 'net_profit_loss',
      key: 'net_profit_loss',
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>
          ${parseFloat(text).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Net P/L per Buyer',
      dataIndex: 'net_profit_loss_per_buyer',
      key: 'net_profit_loss_per_buyer',
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>
          ${parseFloat(text).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'LTV FE P/L per Buyer',
      dataIndex: 'fe_pl_per_buyer_ltv',
      key: 'fe_pl_per_buyer_ltv',
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>
          ${parseFloat(text).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'LTV BE P/L per Buyer',
      dataIndex: 'be_pl_per_buyer_ltv',
      key: 'be_pl_per_buyer_ltv',
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>
          ${parseFloat(text).toFixed(2)}
        </Tag>
      ),
    },

    {
      title: 'LTV Delta',
      dataIndex: 'delta',
      key: 'delta',
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>
          ${parseFloat(text).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="text" 
          danger 
          icon={<DeleteOutlined />} 
          onClick={() => handleRemoveSegment(record.id)}
        />
      ),
    },
  ];

  // Handle campaign tab change
  const handleCampaignChange = (campaignId) => {
    if (campaignId && campaignSegments[campaignId]) {
      setActiveCampaign(campaignId);
      const segs = Array.isArray(campaignSegments[campaignId])
        ? campaignSegments[campaignId]
        : (campaignSegments[campaignId].segments || []);
      setSelectedSegments(segs);
    }
  };

  // No longer needed with Tabs component
  // Keeping this comment for documentation
  
  // Render campaign statistics
  const renderCampaignStats = (campaignId) => {
    console.log(campaignId, "campaignId")
    if (!campaignId || !campaignStats[campaignId]) return null;
    
    const stats = campaignStats[campaignId];
    console.log(stats, "stats")
    
    return (
      <div className="statistics-container">
        <Typography.Title level={5} style={{ marginBottom: 16 }}>Summary Statistics</Typography.Title>
        
        {/* First row - Key metrics */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Total Mailed Quantity" 
                value={stats.totalMailedQuantity} 
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Total Gross Orders" 
                value={stats.totalGrossOrders} 
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Avg Gross %" 
                value={(stats.averageGrossPercentage * 100).toFixed(2)} 
                suffix="%"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Average Performance" 
                value={stats.averagePerformance} 
                precision={2}
                prefix="$"
                valueStyle={{ color: stats.averagePerformance >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
        
        {/* Second row - Financial metrics */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Total Net Profit/Loss" 
                value={stats.totalNetProfitLoss} 
                precision={2}
                prefix="$"
                valueStyle={{ color: stats.totalNetProfitLoss >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Avg P/L per Buyer" 
                value={stats.averagePLPerBuyer} 
                precision={2}
                prefix="$"
                valueStyle={{ color: stats.averagePLPerBuyer >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="Total Delta" 
                value={stats.totalDelta} 
                precision={2}
                prefix="$"
                valueStyle={{ color: stats.totalDelta >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
        
        {/* Third row - Additional metrics */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="LTV FE P/L per Buyer" 
                value={stats.totalPLPerBuyerTotal} 
                precision={2}
                prefix="$"
                valueStyle={{ color: stats.totalPLPerBuyerTotal >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card className="stat-card" size="small" bordered={false}>
              <Statistic 
                title="LTV BE P/L per Buyer" 
                value={stats.totalPLPerBuyer} 
                precision={2}
                prefix="$"
                valueStyle={{ color: stats.totalPLPerBuyer >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };
  
  // Render campaign segments table
  const renderCampaignTable = (campaignId) => {
    if (!campaignId || !campaignSegments[campaignId]) return null;
    
    const segments = Array.isArray(campaignSegments[campaignId])
      ? campaignSegments[campaignId]
      : (campaignSegments[campaignId].segments || []);
    
    return (
      <>
        <Divider style={{ margin: '24px 0' }} />
        <Typography.Title level={5} style={{ marginBottom: 16 }}>Segment Details</Typography.Title>
        <Table 
          columns={columns} 
          dataSource={segments}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </>
    );
  };
  
  return (
    <Card 
      title="Selected Segments by Campaign" 
      bordered={false}
      className="segment-summary-card"
      extra={
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleResetAll}
          disabled={campaigns.length === 0}
          type="primary"
          ghost
        >
          Reset All Campaigns
        </Button>
      }
    >
      {campaigns.length > 0 ? (
        <Tabs
          activeKey={activeCampaign}
          onChange={handleCampaignChange}
          type="card"
          items={campaigns.map(campaign => ({
            key: campaign.id,
            label: campaign.name,
            children: (
              <div className="campaign-tab-content">
                <div className="campaign-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography.Title level={4}>{campaign.name}</Typography.Title>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => handleResetCampaign(campaign.id)}
                    disabled={!((Array.isArray(campaignSegments[campaign.id])
                      ? campaignSegments[campaign.id]?.length
                      : (campaignSegments[campaign.id]?.segments?.length || 0)) > 0)}
                    type="default"
                    size="small"
                  >
                    Clear Campaign Lists
                  </Button>
                </div>
                
                {((Array.isArray(campaignSegments[campaign.id])
                  ? campaignSegments[campaign.id]?.length
                  : (campaignSegments[campaign.id]?.segments?.length || 0)) > 0) ? (
                  <>
                    {renderCampaignStats(campaign.id)}
                    {renderCampaignTable(campaign.id)}
                  </>
                ) : (
                  <Empty description={`No segments selected for ${campaign.name}`} />
                )}
              </div>
            )
          }))}
        />
      ) : (
        <Empty description="No campaigns with segments" />
      )}
    </Card>
  );
};

// Static method to add a segment to a specific campaign in localStorage
SelectedSegmentSummary.addSegment = (segment, campaignId, campaignName) => {
  console.log("Adding segment to campaign:", campaignId, segment);
  try {
    if (!campaignId) {
      console.error('No campaign ID provided for segment');
      return false;
    }
    
    // Get current campaign segments from localStorage
    const savedCampaignSegments = localStorage.getItem('campaignSegments');
    let campaignSegments = savedCampaignSegments ? JSON.parse(savedCampaignSegments) : {};
    console.log("Campaign segments:", campaignSegments);
    // Initialize or normalize campaign storage shape
    if (!campaignSegments[campaignId]) {
      campaignSegments[campaignId] = {
        campaignName: campaignName || `Campaign ${campaignId}`,
        segments: []
      };
    } else if (Array.isArray(campaignSegments[campaignId])) {
      // Normalize array -> object-with-segments
      campaignSegments[campaignId] = {
        campaignName: campaignName || `Campaign ${campaignId}`,
        segments: campaignSegments[campaignId]
      };
    }
    
    // Check if segment already exists in this campaign
    const exists = (campaignSegments[campaignId].segments || []).some(s => 
      s.list_name === segment.list_name && 
      s.segment_name === segment.segment_name && 
      s.mailed_quantity === segment.mailed_quantity && 
      s.performance === segment.performance
    );
    
    if (!exists) {
      // Add new segment with all fields
      const newSegment = {
        ...segment,
        // Ensure all fields exist with defaults if missing
        gross_orders: segment.gross_orders || 0,
        gross_percentage: segment.gross_percentage || 0,
        net_profit_loss: segment.net_profit_loss || 0,
        net_profit_loss_per_buyer: segment.net_profit_loss_per_buyer || 0,
        pl_per_buyer: segment.pl_per_buyer || 0,
        pl_per_buyer_total: segment.pl_per_buyer_total || 0,
        performance_field: segment.performance_field || 'net_profit_loss',
        delta: segment.delta || 0
      };
      
      // Add segment to campaign
      if (!campaignSegments[campaignId].segments) {
        campaignSegments[campaignId].segments = [];
      }
      campaignSegments[campaignId].segments.push(newSegment);
      
      // Save back to localStorage
      localStorage.setItem('campaignSegments', JSON.stringify(campaignSegments));
      
      // For backward compatibility, also update the legacy storage
      // This ensures older code still works
      localStorage.setItem('selectedSegments', JSON.stringify(campaignSegments[campaignId].segments));
      
      return true; // Successfully added
    }
    
    return false; // Segment already exists
  } catch (error) {
    console.error('Error adding segment to campaign in localStorage:', error);
    return false;
  }
};

// Static method to get segments for a specific campaign
SelectedSegmentSummary.getSegmentsForCampaign = (campaignId) => {
  try {
    const savedCampaignSegments = localStorage.getItem('campaignSegments');
    if (savedCampaignSegments && campaignId) {
      const campaignSegments = JSON.parse(savedCampaignSegments);
      if (!campaignSegments[campaignId]) return [];
      return Array.isArray(campaignSegments[campaignId])
        ? campaignSegments[campaignId]
        : (campaignSegments[campaignId].segments || []);
    }
    return [];
  } catch (error) {
    console.error('Error getting segments for campaign:', error);
    return [];
  }
};

// Static method to clear segments for a specific campaign
SelectedSegmentSummary.clearCampaignSegments = (campaignId) => {
  try {
    if (!campaignId) return false;
    
    const savedCampaignSegments = localStorage.getItem('campaignSegments');
    if (savedCampaignSegments) {
      const campaignSegments = JSON.parse(savedCampaignSegments);
      
      if (campaignSegments[campaignId]) {
        if (Array.isArray(campaignSegments[campaignId])) {
          campaignSegments[campaignId] = [];
        } else {
          campaignSegments[campaignId].segments = [];
        }
        localStorage.setItem('campaignSegments', JSON.stringify(campaignSegments));
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Error clearing campaign segments:', error);
    return false;
  }
};



export default SelectedSegmentSummary;
